'use client'

import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {motion} from "framer-motion";
import {Image} from "primereact/image";
import {Apple, Facebook, GitHub, Google, Visibility, VisibilityOff} from "@mui/icons-material";
import Error from "@/components/shared/error";
import React, {useEffect, useState} from "react";
import {useRouter} from "next/navigation";
import {useTranslation} from "react-i18next";
import {ERROR_PROPS} from "@/components/shared/type";
import {FIELDS, SERVER_ERROR_PROPS, USER_LOGIN} from "@/components/admin/login/types";
import {useProcessLoginMutation} from "@/redux/rtk/login/login";
import {analyzeDataFromServer, formatDate} from "@/components/shared/client/utils";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {settingsSelectors, updateGlobalLoading} from "@/redux/slices/settings";
import {useGetActiveCyclesQuery, useVerifyDOBIfEligibleMutation} from "@/redux/rtk/students/shared";
import Dialogs from "@/components/dialogs";
import {DialogControl} from "@/components/dialogs/types";
import CustomFormNew from "@/components/form/customForNew";
import {CYCLE_PROPS} from "@/app/admin/cycles/page";
import {defaultLoginBackgroundImage} from "@/components/constants";

const StudentLoginFields = ({setStep, fields, setFields}: FIELDS) => {

    const {theme: selectedTheme, webContext, siteImages} = useSelector((state) => {
        const webContext = settingsSelectors.getWebContext(state);
        const theme = settingsSelectors.getTheme(state);
        const siteImages = settingsSelectors.getSiteImages(state);
        return {webContext, theme, siteImages};
    }, shallowEqual);
    const dispatch = useDispatch();
    const router = useRouter();
    const theme = useTheme();
    const {t} = useTranslation("login");
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showPassword, setShowPassword] = useState(false);
    const [openDialog, setOpenDialog] = useState<DialogControl>({open: false});
    const [err, setErr] = useState<ERROR_PROPS | null>(null);
    const [cycleRecords, setCycleRecords] = useState<CYCLE_PROPS[]>([]);
    const [formData, setFormData] = useState<USER_LOGIN>({
        email: "",
        password: "",
        rememberMe: false,
    });
    const [errors, setErrors] = useState({
        email: "",
        password: "",
    });

    const {data: cyclesData, isLoading} = useGetActiveCyclesQuery(null);
    const [verifyDOBIfEligible, {error: verificationError, reset, isSuccess, isLoading: DOBVerificationLoading}] = useVerifyDOBIfEligibleMutation();
    const [processLogin, {data, error, isLoading: loading}] = useProcessLoginMutation();

    useEffect(() => {
        if (cyclesData?.data?.data?.pRecords?.length) {
            const data = cyclesData.data.data.pRecords;
            setCycleRecords(data);
            setFields(state => ({
                ...state,
                cycleId: data[0].uid
            }))
        }
    }, [cyclesData])

    useEffect(() => {
        if (data?.data?.data) {
            const res = analyzeDataFromServer(data.data.data);
            if (res?.error) {
                setErr(res)
            }
            if (res?.redirect) {
                dispatch(updateGlobalLoading(true));
                router.push(res.redirectTo)
            }
        }
    }, [data]);

    useEffect(() => {
        if(isSuccess) {
            setStep(4);
            reset();
        }
    }, [isSuccess])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value, checked} = e.target
        setFormData({
            ...formData,
            [name]: name === "rememberMe" ? checked : value,
        })

        if (errors[name as keyof typeof errors]) {
            setErrors({
                ...errors,
                [name]: "",
            })
        }
    }

    const validateForm = () => {
        let valid = true
        const newErrors = {email: "", password: ""}

        // email validation
        if (!formData.email) {
            newErrors.email = t("emailIsRequired" as any)
            valid = false;
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
            newErrors.email = t('emailIsInvalid' as any)
            valid = false
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = t('passwordIsRequired' as any)
            valid = false
        } else if (formData.password.length < 8) {
            newErrors.password = t('passwordCheck_1')
            valid = false
        }

        setErrors(newErrors)
        return valid
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return

        void processLogin(formData)
    }

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    const showClassSelectionModal = () => {
        reset();
        setOpenDialog(state => ({
            ...state,
            open: true
        }))
    }

    const handleDialogClose = () => {
        reset();
        setOpenDialog({
            open: false
        });
    }

    const handleDOBCheck = () => {
        const cls: any = cycleRecords.find((rec: CYCLE_PROPS) => rec.uid === fields.cycleId);
        if(cls) {
            void verifyDOBIfEligible({
                cycleId: cls.uid,
                dob: formatDate(new Date(fields.dateOfBirth!))
            })
        }
    }

    return <Grid container className="df jcc aic we1 mhe1 hfc">

        <Dialogs
            open={openDialog}
            children={
                <Grid container className="df we1 fdc">

                    <CustomFormNew
                        t={t}
                        loading={false}
                        apiLoading={DOBVerificationLoading}
                        error={verificationError as SERVER_ERROR_PROPS}
                        formItemGlobalStyles={{
                            marginTop: "1rem",
                            width: "100%"
                        }}
                        cancelButton={{
                            label: t("close"),
                            onClick: handleDialogClose
                        }}
                        onValidSubmit={handleDOBCheck}
                        values={fields}
                        setValues={setFields}
                        formItems={[
                            {
                                items: [
                                    {
                                        label: t("selectClass"),
                                        type: "select",
                                        name: "cycleId",
                                        options: cycleRecords.map((rec: CYCLE_PROPS) => {
                                            return {
                                                value: rec.uid!,
                                                label: rec.className!
                                            }
                                        }),
                                        validation: true
                                    },
                                    {
                                        label: t("global:dateOfBirth"),
                                        type: "date",
                                        name: "dateOfBirth",
                                        validation: true,
                                        validationType: "date",
                                        shouldDisableFuture: true,
                                        validationData: {
                                            type: "yearsInFuture",
                                            numberOfYears: 10
                                        }
                                    }
                                ],
                                size: {
                                    xs: 12
                                }
                            }
                        ]}
                    />

                </Grid>
            }
            handleClose={handleDialogClose}
            title={t('selectDateOfBirth')}
        >

        </Dialogs>

        <Grid
            size={{xs: false, md: 6, lg: 8}}
            className="df jcc aic"
            sx={{
                alignSelf: "stretch",
                ...!isMobile ? {
                    backgroundImage: siteImages?.images?.loginImage ? `url(${siteImages.dir}${siteImages.images.loginImage}1.webp)` : defaultLoginBackgroundImage,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundColor: (theme) => theme?.palette?.backgrounds?.secondary ?? "#f4f4f4"
                } : {}
            }}
        >
        </Grid>
        <Grid className="jcc aic df" size={{xs: 12, md: 6, lg: 4}}
               sx={{background: theme => theme?.palette?.backgrounds?.primary ?? "#f4f4f4"}}>

            <Grid container sx={{
                width: {
                    xs: "100%",
                    sm: "30rem",
                },
                background: theme => theme?.palette?.backgrounds?.primary ?? "#f4f4f4"
            }}>
                <Box
                    className="he1 df fdc aic jcc we1"
                    sx={{
                        borderRadius: "10px",
                        p: 4
                    }}
                >
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5}}
                        style={{width: "100%"}}
                    >
                        {(webContext?.logo_1_url && webContext?.logo_2_url) ?
                            <Box className="df jcc">
                                <Image
                                    src={selectedTheme === 1 ? `${webContext.logo_2_url}1.webp` : `${webContext.logo_1_url}1.webp`}
                                    alt="Company Logo"
                                    width={"120px"}
                                />
                            </Box> : <div></div>}
                        <Box sx={{mb: 5, mt: 4}}>
                            <Typography
                                variant="h6"
                                component="h1"
                                fontWeight="bold"
                                sx={{color: (theme: Theme) => theme?.palette?.texts?.main ?? "#323232", mb: 1, mt: 2}}
                            >
                                {t('welcomeToMCJRegistration')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t('singInToContinue')}
                            </Typography>
                        </Box>

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    label={t('emailAddress')}
                                    name="email"
                                    type="email"
                                    variant="outlined"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    InputProps={{
                                        sx: {borderRadius: 2},
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label={t('global:password')}
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    variant="outlined"
                                    value={formData.password}
                                    onChange={handleChange}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    InputProps={{
                                        sx: {borderRadius: 2},
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleTogglePasswordVisibility}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexDirection: isMobile ? "column" : "row",
                                        gap: isMobile ? 1 : 0,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="rememberMe"
                                                checked={formData.rememberMe}
                                                onChange={handleChange}
                                                color="primary"
                                            />
                                        }
                                        label={t('rememberMe')}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        sx={{color: "primary.main", fontWeight: 500, cursor: "pointer"}}
                                        onClick={() => setStep(2)}
                                    >
                                        {t('forgotPassword')}?
                                    </Typography>
                                </Box>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        textTransform: "none",
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        boxShadow: "0 8px 16px rgba(0, 13, 255, 0.2)"
                                    }}
                                >
                                    {loading ? `${t('signingIn')}...` : t('signIn')}
                                </Button>
                            </Stack>
                        </form>

                        {(!!error || !!err) && <Error error={(error || err) as SERVER_ERROR_PROPS} timeout={10000}/>}

                        {(!isLoading && cycleRecords?.length > 0) ? <Grid container className="df jcc" sx={{fontSize: "1.2rem", mt: 3}}>
                            <Grid>{t('doNotHaveAccount')}</Grid>
                            <Grid
                                className="fwb cp"
                                sx={{ml: 0.5, mr: 0.5, color: "primary.main"}}
                                onClick={showClassSelectionModal}
                            >
                                {t('global:register')}
                            </Grid>
                        </Grid> : (!isLoading && cycleRecords?.length === 0) &&
                            <Grid className="df jcc" sx={{mt: 3}}>
                                <Typography variant="h6" color="error">{t('registrationHasBeenClosed')}</Typography>
                            </Grid>
                        }

                    </motion.div>
                </Box>
            </Grid>

        </Grid>
    </Grid>
}

export default StudentLoginFields;